import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { BeaconDTO } from "src/app/beacons/Beacon";
import { BeaconRegionDTO } from "../BeaconRegion";
import { TitleService } from "src/app/title.service";
import { MatDialog } from "@angular/material/dialog";
import { SessionService } from "src/app/session.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { BeaconRegionsService } from "src/app/beacon-regions/beacon-regions.service";
import { filter, find, remove } from "lodash";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { MatChipInputEvent } from "@angular/material/chips";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { UiService } from "src/app/ui/ui.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

@Component({
  selector: "app-beacon-region",
  templateUrl: "./beacon-region.component.html",
  styleUrls: ["./beacon-region.component.scss"],
})
export class BeaconRegionComponent implements OnInit {
  newBeaconRegion!: boolean;
  beaconRegion?: BeaconRegionDTO;
  beaconRegionForm!: FormGroup;
  beacons?: BeaconDTO[];
  beaconSearchText: string = "";
  unassignedBeacons!: BeaconDTO[];
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  hasBeaconRegionRemove: boolean = false;
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private TitleService: TitleService,
    private dialog: MatDialog,
    private router: Router,
    private BeaconRegionsService: BeaconRegionsService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.beaconRegion = this.activatedRoute.snapshot.data.beaconRegion;
    this.newBeaconRegion = !this.beaconRegion;

    this.unassignedBeacons = filter(
      this.activatedRoute.snapshot.data.beacons.records,
      (b) => {
        if (
          this.beaconRegion?.beacons == undefined ||
          this.beaconRegion.beacons.length == 0
        ) {
          return true;
        }
        return (
          find(this.beaconRegion?.beacons, (ab) => {
            return ab.id != b.id;
          }) != undefined
        );
      },
    );

    if (!this.newBeaconRegion) {
      this.TitleService.setTitle(
        this.TitleService.getTitle() + ` | ${this.beaconRegion?.name}`,
      );
    }

    this.initForms();

    this.hasBeaconRegionRemove =
      this.activatedRoute.snapshot.data.permissions.hasBeaconRegionRemove;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initForms() {
    this.beaconRegionForm = new FormGroup({
      name: new FormControl(this.beaconRegion?.name, Validators.required),
      disabled: new FormControl(this.beaconRegion?.disabled),
      tags: new FormControl(this.beaconRegion?.tags),
      entry_delay_seconds: new FormControl(
        this.beaconRegion?.entry_delay_seconds,
      ),
      timeout: new FormControl(this.beaconRegion?.timeout),
      uuid: new FormControl(this.beaconRegion?.uuid, (control) => {
        const value = control.value;
        if (value === null || value === undefined || value === "") {
          return null;
        }
        return UUID_V4_REGEX.test(String(value))
          ? null
          : { uuid: "UUID must be a valid GUID" };
      }),
      major: new FormControl(this.beaconRegion?.major, [
        Validators.min(0),
        Validators.max(65535),
      ]),
      minor: new FormControl(this.beaconRegion?.minor, [
        Validators.min(0),
        Validators.max(65535),
      ]),
      proximity_tag: new FormControl(this.beaconRegion?.proximity_tag),
      proximity_range: new FormControl(this.beaconRegion?.proximity_range),
    });
  }

  get beaconRegionDTO() {
    let beaconRegionDTO = {
      name: this.beaconRegionForm.get("name")?.value,
      disabled: this.beaconRegionForm.get("disabled")?.value || false,
      timeout: this.beaconRegionForm.get("timeout")?.value || 10800,
      entry_delay_seconds:
        this.beaconRegionForm.get("entry_delay_seconds")?.value || 0,
      uuid: this.beaconRegionForm.get("uuid")?.value ?? null,
      major: this.beaconRegionForm.get("major")?.value ?? null,
      minor: this.beaconRegionForm.get("minor")?.value ?? null,
      proximity_tag: this.beaconRegionForm.get("proximity_tag")?.value || null,
      proximity_range:
        this.beaconRegionForm.get("proximity_range")?.value ?? null,
    };

    if (this.beaconRegion) {
      beaconRegionDTO = { ...this.beaconRegion, ...beaconRegionDTO };
    }

    return beaconRegionDTO;
  }

  get saveDisabled() {
    return this.beaconRegionForm.invalid;
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get showDelete() {
    return this.hasBeaconRegionRemove;
  }

  addTag(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || "").trim()) {
      this.beaconRegionForm.controls.tags.value.push(value.trim());
    }

    if (input) {
      input.value = "";
    }
  }

  removeTag(tag: string): void {
    const index = this.beaconRegionForm.controls.tags.value.indexOf(tag);

    if (index! >= 0) {
      this.beaconRegionForm.controls.tags.value.splice(index!, 1);
    }
  }

  saveNewBeaconRegion() {
    this.uiService
      .waitForEvent(
        this.BeaconRegionsService.createBeaconRegion(
          this.currentAccount.accountID,
          this.beaconRegionDTO as BeaconRegionDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (saved) => {
          let br: BeaconRegionDTO = <BeaconRegionDTO>(<unknown>saved);
          this.NotificationService.openSuccessSnackBar(
            "Beacon region created successfully",
          );
          this.router.navigate([br.id], {
            relativeTo: this.activatedRoute.parent,
          });
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error creating beacon region",
          );
        },
      );
  }

  saveBeaconRegionUpdates() {
    this.uiService
      .waitForEvent(
        this.BeaconRegionsService.updateBeaconRegion(
          this.currentAccount.accountID,
          this.beaconRegionDTO as BeaconRegionDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar(
            "Beacon region updated successfully",
          );
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error updating beacon region",
          );
        },
      );
  }

  cancel() {
    this.router.navigate(["list"], {
      relativeTo: this.activatedRoute.parent!.parent,
    });
  }

  deleteBeaconRegion() {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this beacon region( ${
              this.beaconRegion!.name
            } )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.BeaconRegionsService.deleteBeaconRegion(
                this.currentAccount.accountID,
                this.beaconRegion!.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar(
                  "Beacon region deleted",
                );

                this.router.navigate(["list"], {
                  relativeTo: this.activatedRoute.parent!.parent,
                });
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting beacon region",
                );
              },
            );
        }
      });
  }

  onDropBeacon(event: CdkDragDrop<any>) {
    // ignore if container has not changed
    if (event.container.data.list === event.previousContainer.data.list) {
      return;
    }

    // if target is unassigned, remove from beacon and add to unassigned (same as removal by click)
    if (event.container.data.list == "unassigned") {
      this.onBeaconRemove(event.item.data);
    }

    // if source was unassigned
    else if (event.container.data.list == "assigned") {
      // remove from unassigned
      remove(this.unassignedBeacons, (b) => {
        return b.id == event.item.data.id;
      });
      this.beaconRegion?.beacons?.push(event.item.data);
    }
  }

  onBeaconRemove(beacon: BeaconDTO) {
    // Remove from assigned
    remove(this.beaconRegion!.beacons!, (b) => {
      return b.id == beacon.id;
    });
    // Add to unassigned
    this.unassignedBeacons.push(beacon);
  }
}
